import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  structuredData?: object | object[];
}

export default function SEO({
  title,
  description,
  keywords,
  ogImage = 'https://public.readdy.ai/ai/img_res/e64c32c52996b8a9354acb1518c3dbb3.jpg',
  ogType = 'website',
  canonical,
  structuredData,
}: SEOProps) {
  useEffect(() => {
    // Set document title
    document.title = title;

    // Helper function to set or update meta tags
    const setMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Generate canonical URL using environment variable
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const canonicalUrl = canonical ? `${siteUrl}${canonical}` : window.location.href;

    // Set basic meta tags
    setMetaTag('description', description);
    if (keywords) {
      setMetaTag('keywords', keywords);
    }

    // Set Open Graph tags
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', canonicalUrl, true);

    // Set Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);

    // Set canonical URL
    let linkElement = document.querySelector('link[rel="canonical"]');
    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      document.head.appendChild(linkElement);
    }
    linkElement.setAttribute('href', canonicalUrl);

    // Set structured data
    if (structuredData) {
      // Remove existing structured data scripts
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => script.remove());

      // Add new structured data
      try {
        const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
        dataArray.forEach(data => {
          const scriptElement = document.createElement('script');
          scriptElement.setAttribute('type', 'application/ld+json');
          scriptElement.textContent = JSON.stringify(data);
          document.head.appendChild(scriptElement);
        });
      } catch (error) {
        console.error('Error setting structured data:', error);
      }
    }
  }, [title, description, keywords, ogImage, ogType, canonical, structuredData]);

  return null;
}
